import React from 'react';
import { Button, Icon } from 'ui';

import useChatPracticeProposalCard from './useChatPracticeProposalCard';

import './_chat-practice-proposal-card.scss';

type ChatPracticeProposalCardProps = {
  conversationId: string | null;
  messageId: string;
  proposalId: string;
};

/**
 * ChatPracticeProposalCard - Compact card of a practice the study tutor proposed (TANAI-2616),
 * rendered inside the assistant bubble. Renders nothing while the proposal loads, when the server
 * does not return it (404), when the feature flag is off, and in the native app or Teams.
 *
 * The status line is a live region rendered in every state, also while empty, so a change after
 * the student comes back from the practice tab («Preparando…» → «3 de 10 respondidas») is announced.
 */
function ChatPracticeProposalCard({ conversationId, messageId, proposalId }: Readonly<ChatPracticeProposalCardProps>): JSX.Element | null {
  const { blockedTabMessage, isVisible, lessons, primaryButton, secondaryButton, status, statusText, title } = useChatPracticeProposalCard({ conversationId, messageId, proposalId });

  if (!isVisible) return null;

  return (
    <div aria-label={title} className={`chat-practice-proposal-card chat-practice-proposal-card--${status}`} role="group">
      <div className="chat-practice-proposal-card__header">
        <span className="chat-practice-proposal-card__icon">
          <Icon name="rayo" size="md" />
        </span>
        <p className="chat-practice-proposal-card__title">{title}</p>
      </div>

      {lessons.length > 0 && (
        <ul className="chat-practice-proposal-card__lessons">
          {lessons.map((lesson) => (
            <li className="chat-practice-proposal-card__lesson" key={lesson.id}>
              {lesson.title}
            </li>
          ))}
        </ul>
      )}

      <p className="chat-practice-proposal-card__status" role="status">
        {statusText}
      </p>

      {(primaryButton || secondaryButton) && (
        <div className="chat-practice-proposal-card__actions">
          {primaryButton && <Button onClick={primaryButton.onSelect} size="sm" text={primaryButton.label} variant="primary" />}
          {secondaryButton && <Button onClick={secondaryButton.onSelect} size="sm" text={secondaryButton.label} variant="primary-outline" />}
        </div>
      )}

      {blockedTabMessage && (
        <p className="chat-practice-proposal-card__blocked" role="alert">
          {blockedTabMessage}
        </p>
      )}
    </div>
  );
}

export default ChatPracticeProposalCard;
